import React from "react";

import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import BulkMessageModal from "@ats/src/components/modals/BulkMessageModal";
import BulkMoveModal from "@ats/src/components/modals/BulkMoveModal";
import BulkGenerateAiSummariesConfirmModal from "@ats/src/views/jobApplications/BulkGenerateAiSummariesConfirmModal";
import PlanBadge from "@ats/src/components/shared/PlanBadge";
import styled from "@emotion/styled";

import { hiringStagesForJob } from "@ats/src/lib/utils/helpers";

import { useModalContext } from "@shared/context/ModalContext";
import { ListState } from "@shared/hooks/useChecklist";
import { PlanFeatures } from "@shared/types/organization";
import { useFeatureGate } from "@shared/hooks/useFeatureGate";
import { trackEvent } from "@shared/lib/posthog";
import { bulkSummaryProcessableCount } from "@shared/lib/bulkAiSummaryCount";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";

function JobStageMenu(props: {
  job?: { id: number };
  currentStage: any;
  jobApplications: [any];
  listState: ListState;
  appliedRoleFitFilters?: Set<string>;
  appliedKnockoutPassFilters?: Set<string>;
  appliedKnockoutFailFilters?: Set<string>;
  appliedKnockoutNoReplyFilters?: Set<string>;
  appliedSourceFilters?: Set<string>;
  appliedHasResumeFilter?: boolean | null;
  appliedCreatedViaFilters?: Set<string>;
  selectableCount?: number;
  sort: string;
  sortDirection: string;
  history: History;
  resetList: () => void;
  location?: any;
}) {
  const {
    jobApplications,
    job,
    currentStage,
    listState: { itemIdsSet, itemIdsSetType, allSelected },
    appliedRoleFitFilters,
    appliedKnockoutPassFilters,
    appliedKnockoutFailFilters,
    appliedKnockoutNoReplyFilters,
    appliedSourceFilters,
    appliedHasResumeFilter,
    appliedCreatedViaFilters,
    selectableCount,
    sort,
    sortDirection,
    history,
    location,
  } = props;
  const { openModal, removeModal } = useModalContext();

  const { getFeatureInfo } = useFeatureGate();
  const bulkMessageFeature = getFeatureInfo(PlanFeatures.BULK_MESSAGE);

  // const job = useJobStore(React.useCallback((state) => state.jobsById[jobId], [jobId])) as any;
  // Get the Job Application to move focus to after move
  const firstJobApplicationNotSelected = React.useMemo(() => {
    if (itemIdsSetType === "included") {
      return jobApplications.find((jobApplication) => !itemIdsSet.has(jobApplication.id));
    } else {
      return jobApplications.find((jobApplication) => itemIdsSet.has(jobApplication.id));
    }
  }, [jobApplications, itemIdsSetType, itemIdsSet]);

  const firstRemainingJobApplicationUrl =
    firstJobApplicationNotSelected != undefined &&
    `/jobs/${firstJobApplicationNotSelected.jobId}/stages/${firstJobApplicationNotSelected.hiringStageId}/applicants/${firstJobApplicationNotSelected.id}`;

  const hiringStages = React.useMemo(() => hiringStagesForJob(job), [job]);
  const itemIds = Array.from(itemIdsSet);
  // Active filters, forwarded to bulk actions so a "select all" on a filtered
  // stage resolves to the same filtered set on the backend. Role fit travels as an
  // array; each knockout condition travels as one comma-joined string of question ids.
  // Source and created-via travel as arrays of served values; has resume as one
  // boolean, or null for unset.
  const roleFit =
    appliedRoleFitFilters && appliedRoleFitFilters.size ? Array.from(appliedRoleFitFilters) : [];
  const knockoutPass =
    appliedKnockoutPassFilters && appliedKnockoutPassFilters.size
      ? Array.from(appliedKnockoutPassFilters).join(",")
      : "";
  const knockoutFail =
    appliedKnockoutFailFilters && appliedKnockoutFailFilters.size
      ? Array.from(appliedKnockoutFailFilters).join(",")
      : "";
  const knockoutNoReply =
    appliedKnockoutNoReplyFilters && appliedKnockoutNoReplyFilters.size
      ? Array.from(appliedKnockoutNoReplyFilters).join(",")
      : "";
  const source =
    appliedSourceFilters && appliedSourceFilters.size ? Array.from(appliedSourceFilters) : [];
  const hasResume = appliedHasResumeFilter != undefined ? appliedHasResumeFilter : null;
  const createdVia =
    appliedCreatedViaFilters && appliedCreatedViaFilters.size
      ? Array.from(appliedCreatedViaFilters)
      : [];

  // window.logger("%c[JobStageMenu] Selection ", "background: #EFDDEF; color: #197682", {
  //   allSelected,
  //   currentStageCount: currentStage.jobApplicationsCount,
  //   setSize: itemIdsSet.size,
  //   firstJobApplicationNotSelected,
  // });

  // Selectable total = the filtered count from the list query (meta.count), so a
  // Select-All while filtered counts the filtered set, not the whole stage.
  // Falls back to the unfiltered stage count before the list has loaded.
  const stageSelectableCount =
    selectableCount != null ? selectableCount : currentStage.jobApplicationsCount;
  const candidatesCount = allSelected
    ? stageSelectableCount
    : itemIdsSetType === "included" && itemIds.length > 0
    ? itemIds.length
    : itemIdsSetType === "excluded" && itemIds.length > 0
    ? stageSelectableCount - itemIds.length
    : 0;

  // How many of the selection a bulk AI summary run will actually process —
  // candidates with a "current" summary are excluded at enqueue. isExact is
  // false for a Select-All that isn't fully loaded (count is an upper bound).
  const { count: processableSummaryCount, isExact: isProcessableCountExact } =
    bulkSummaryProcessableCount({
      jobApplications,
      itemIdsSet,
      itemIdsSetType,
      selectionCount: candidatesCount,
      selectableCount: stageSelectableCount,
    });

  const handleOnClickMessageAll = (event) => {
    event.preventDefault();
    trackEvent("bulk_message_clicked", { job_id: job.id, hiring_stage_id: currentStage.id, candidates_count: candidatesCount });

    const modal = (
      <BulkMessageModal
        jobId={job.id}
        hiringStageId={currentStage.id}
        onCancel={() => removeModal()}
        jobApplications={jobApplications}
        idsArray={itemIds}
        idsArrayType={itemIdsSetType}
        roleFit={roleFit}
        knockoutPass={knockoutPass}
        knockoutFail={knockoutFail}
        knockoutNoReply={knockoutNoReply}
        source={source}
        hasResume={hasResume}
        createdVia={createdVia}
        candidatesCount={candidatesCount}
        history={history}
        location={location}
      />
    );

    openModal(modal);
  };

  const handleOnClickGenerateAiSummaries = (event) => {
    event.preventDefault();
    trackEvent("bulk_generate_ai_summaries_clicked", { job_id: job.id, hiring_stage_id: currentStage.id, candidates_count: candidatesCount });

    const modal = (
      <BulkGenerateAiSummariesConfirmModal
        jobId={job.id}
        hiringStageId={currentStage.id}
        onCancel={() => removeModal()}
        idsArray={itemIds}
        idsArrayType={itemIdsSetType}
        roleFit={roleFit}
        knockoutPass={knockoutPass}
        knockoutFail={knockoutFail}
        knockoutNoReply={knockoutNoReply}
        source={source}
        hasResume={hasResume}
        createdVia={createdVia}
        sort={sort}
        sortDirection={sortDirection}
        candidatesCount={candidatesCount}
        processableCount={processableSummaryCount}
        isProcessableCountExact={isProcessableCountExact}
        resetList={props.resetList}
      />
    );

    openModal(modal);
  };

  const handleOnClickMoveAll = (event) => {
    event.preventDefault();
    trackEvent("bulk_move_clicked", { job_id: job.id, hiring_stage_id: currentStage.id, candidates_count: candidatesCount });

    const modal = (
      <BulkMoveModal
        jobId={job.id}
        onCancel={() => removeModal()}
        currentStage={currentStage}
        hiringStages={hiringStages}
        idsArray={itemIds}
        idsArrayType={itemIdsSetType}
        roleFit={roleFit}
        knockoutPass={knockoutPass}
        knockoutFail={knockoutFail}
        knockoutNoReply={knockoutNoReply}
        source={source}
        hasResume={hasResume}
        createdVia={createdVia}
        resetList={props.resetList}
        candidatesCount={candidatesCount}
        firstRemainingJobApplicationUrl={firstRemainingJobApplicationUrl}
        history={history}
        location={location}
      />
    );

    openModal(modal);
  };

  const bulkMessagePlanBadge = bulkMessageFeature.upgradePlanKey ? (
    <PlanBadge
      planKey={bulkMessageFeature.upgradePlanKey}
      planDisplayName={bulkMessageFeature.planDisplayName}
      size="small"
      locked={bulkMessageFeature.isLocked}
      includeTag={false}
    />
  ) : null;

  return (
    <DropdownMenu label="Bulk options" hpos="right">
      <Styled.Button onClick={handleOnClickMessageAll}>
        Message candidates
        {bulkMessagePlanBadge}
      </Styled.Button>
      <Styled.Button onClick={handleOnClickMoveAll}>Move candidates</Styled.Button>
      <FeatureFlipper feature="AI_APPLICANT_SUMMARY">
        <Styled.Button onClick={handleOnClickGenerateAiSummaries}>
          Run Plato reviews
        </Styled.Button>
      </FeatureFlipper>
    </DropdownMenu>
  );
}

JobStageMenu.propTypes = {};

JobStageMenu.defaultProps = {};

export default JobStageMenu;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Button = styled.button`
  padding-right: 0.25rem !important;
`;
